/**
 * Regras puras da abertura do caso por serviço (E126.F2, GUS-105, ADR-0088).
 *
 * Sem JSX, sem hooks, sem I/O. A mesma abertura serve o portal e a área do perito:
 * - três serviços; a morfológica independente aparece e não é contratável até o laudo próprio
 *   ser validado (GUS-114), então não vira modalidade nenhuma;
 * - identificação: o número do processo, a pasta do cliente e o CPF do cliente. Opcional na abertura
 *   interna; obrigatória na do cliente no fluxo eletrônico (E165.F1.T03, ADR-0114 §1). O número,
 *   quando informado, no formato CNJ (a capa do exame lê o juízo dele); o CPF, com os verificadores;
 * - na eletrônica não há subtipo: documental, com biometria ou com fotos é o que o material
 *   mostra depois do envio, não uma escolha.
 */
import type { CaseCreateData, Modalidade } from '@/lib/cases';
import { cpfValido, digitsOnly, formatCpf } from '@/lib/masks';
import type { ProcessoData, ProcessoUpdateData } from '@/lib/processos';
import { CNJ_PLACEHOLDER, CNJ_REGEX } from '@/pages/novo_caso_form';

export type ServicoDaAbertura = 'eletronica' | 'grafotecnica' | 'morfologica';

export interface OpcaoDeServico {
  servico: ServicoDaAbertura;
  /** `false`: não é botão; aparece como aviso neutro (ADR-0088 §2.1; E162.F2.T06, QA-17). */
  contratavel: boolean;
}

export const SERVICOS_DA_ABERTURA: readonly OpcaoDeServico[] = [
  { servico: 'eletronica', contratavel: true },
  { servico: 'grafotecnica', contratavel: true },
  { servico: 'morfologica', contratavel: false },
];

/** A modalidade do caso para o serviço escolhido; `null` para o que não é contratável. */
export function modalidadeDoServico(servico: ServicoDaAbertura): Modalidade | null {
  return servico === 'morfologica' ? null : servico;
}

/**
 * A jornada v6.7 (E144.F3, GUS-135): Enviar documentos → Conferir → Acompanhar. A primeira tela
 * reúne o serviço, os arquivos, a custódia resumida e a identificação; a grafotécnica sai dela
 * para o envio de sempre.
 */
export type PassoDaAbertura = 'enviar' | 'contratos' | 'conferir' | 'acompanhar';

/** E177.F4 (GUS-253, ADR-0130): Serviço → Contratos (um bloco por CCB) → Conferir → Acompanhar. */
export const PASSOS_DA_JORNADA: readonly PassoDaAbertura[] = [
  'enviar',
  'contratos',
  'conferir',
  'acompanhar',
];

/**
 * O passo guardado na URL. Os nomes de antes (Arquivos e Custódia viraram parte do Enviar; o
 * resumo do processo virou Acompanhar) continuam valendo para o link já salvo.
 */
export function passoDaUrl(valor: string | null): PassoDaAbertura {
  if (valor === 'conferir' || valor === 'contratos') return valor;
  return valor === 'acompanhar' || valor === 'processo' ? 'acompanhar' : 'enviar';
}

/**
 * E163.F3 (GUS-209 A67-13/14): a consulta "Cadeia de custódia" abre sobre o passo, em `?vista=`.
 * Abrir empilha no histórico (o voltar do navegador devolve ao passo); fechar tira só a vista.
 */
export const VISTA_DA_CUSTODIA = 'custodia';

export function comACustodia(atual: URLSearchParams, aberta: boolean): URLSearchParams {
  const proximo = new URLSearchParams(atual);
  if (aberta) proximo.set('vista', VISTA_DA_CUSTODIA);
  else proximo.delete('vista');
  return proximo;
}

export interface IdentificacaoMinima {
  pasta: string;
  processo: string;
  /** E165.F1.T03: o CPF do cliente como digitado (com a máscara da tela). */
  cpf: string;
}

export const IDENTIFICACAO_VAZIA: IdentificacaoMinima = { pasta: '', processo: '', cpf: '' };

export type ErroDoCampo = 'obrigatorio' | 'invalido';

/** O erro de cada campo da identificação; `null` é o campo que está certo. */
export interface ErrosDaIdentificacao {
  processo: ErroDoCampo | null;
  pasta: ErroDoCampo | null;
  cpf: ErroDoCampo | null;
}

function erroDoProcesso(processo: string, obrigatoria: boolean): ErroDoCampo | null {
  if (processo.length === 0 || processo === CNJ_PLACEHOLDER) {
    return obrigatoria ? 'obrigatorio' : null;
  }
  return CNJ_REGEX.test(processo) ? null : 'invalido';
}

/**
 * O número informado precisa ser CNJ e o CPF, ter os verificadores certos. Na abertura do cliente
 * no fluxo eletrônico (`obrigatoria`) os três são exigidos, e o travessão de "sem número" não
 * identifica; fora dela, vazio é aceito (o processo pode vir depois).
 */
export function validarIdentificacao(
  identificacao: IdentificacaoMinima,
  obrigatoria = false,
): ErrosDaIdentificacao {
  const cpf = identificacao.cpf.trim();
  return {
    processo: erroDoProcesso(identificacao.processo.trim(), obrigatoria),
    pasta: obrigatoria && identificacao.pasta.trim().length === 0 ? 'obrigatorio' : null,
    cpf:
      cpf.length === 0 ? (obrigatoria ? 'obrigatorio' : null) : cpfValido(cpf) ? null : 'invalido',
  };
}

export function identificacaoValida(
  identificacao: IdentificacaoMinima,
  obrigatoria = false,
): boolean {
  const erros = validarIdentificacao(identificacao, obrigatoria);
  return erros.processo === null && erros.pasta === null && erros.cpf === null;
}

/** A identificação gravada no processo, como a tela a edita (o CPF com a máscara). */
export function identificacaoDoProcesso(
  processo: ProcessoData | null | undefined,
): IdentificacaoMinima {
  return {
    pasta: processo?.pasta ?? '',
    processo: processo?.process_number ?? '',
    cpf: formatCpf(processo?.cpf_informado ?? ''),
  };
}

function textoOuNulo(valor: string): string | null {
  const limpo = valor.trim();
  return limpo.length > 0 ? limpo : null;
}

/** O CPF como o servidor guarda: só os dígitos, ou `null` sem nenhum. */
function cpfOuNulo(valor: string): string | null {
  return textoOuNulo(digitsOnly(valor));
}

/**
 * O pedido de abertura. Sem pasta e sem número, o servidor resolve: o título vira o código do
 * exame. A abertura não manda data nenhuma (E163.F1, ADR-0112): o recebimento é o dia em Brasília,
 * e a data do ato só existe quando o perito a informa na edição do caso.
 */
export function payloadDaAbertura(
  modalidade: Modalidade,
  identificacao: IdentificacaoMinima,
): CaseCreateData {
  const cpf = cpfOuNulo(identificacao.cpf);
  return {
    modalidade,
    title: textoOuNulo(identificacao.pasta),
    process_number: textoOuNulo(identificacao.processo),
    ...(cpf === null ? {} : { cpf_informado: cpf }),
  };
}

/**
 * O pedido de mais um exame num processo existente (E126.F4): o processo traz a pasta e o
 * número, e o servidor recusa os dois junto com `processo_id`.
 */
export function payloadDoExameNoProcesso(
  modalidade: Modalidade,
  processoId: string,
): CaseCreateData {
  const {
    title: _title,
    process_number: _numero,
    ...resto
  } = payloadDaAbertura(modalidade, IDENTIFICACAO_VAZIA);
  void _title;
  void _numero;
  return { ...resto, processo_id: processoId };
}

/** E143.F2: o banco escolhe o escritório só no processo novo — no existente, é o dele. */
export function escolheEscritorio(doBanco: boolean, processo: ProcessoData | undefined): boolean {
  return doBanco && processo === undefined;
}

export interface DadosDoPasso {
  processo: ProcessoData | undefined;
  identificacao: IdentificacaoMinima;
  /** `undefined` fora da abertura pelo banco; `null` enquanto o banco não escolheu. */
  escritorioDoBanco: string | null | undefined;
  /** E144.F3: o prazo processual do pedido; no processo existente, o prazo é o dele. */
  prazo?: string;
  /**
   * E177.F1 (ADR-0131): o escritório requerente do caso que a operação abre; `undefined` fora da
   * plataforma, `null` sem escritório. O banco não vai: sai da leitura da CCB.
   */
  escritorioRequerente?: string | null | undefined;
}

export function payloadDoPasso(modalidade: Modalidade, dados: DadosDoPasso): CaseCreateData {
  const { processo, identificacao, escritorioDoBanco } = dados;
  if (processo !== undefined) return payloadDoExameNoProcesso(modalidade, processo.id);
  const prazo = textoOuNulo(dados.prazo ?? '');
  const payload = {
    ...payloadDaAbertura(modalidade, identificacao),
    ...(prazo === null ? {} : { deadline: prazo }),
    ...(dados.escritorioRequerente ? { origem_escritorio_id: dados.escritorioRequerente } : {}),
  };
  return escritorioDoBanco === undefined
    ? payload
    : { ...payload, escritorio_id: escritorioDoBanco };
}

/**
 * O pedido do cliente no processo (E144.F3, ADR-0098 §2.6): o prazo processual (data e, se houver,
 * hora), o pedido de urgência com o motivo e a observação para a equipe. Na tela, texto; vazio é
 * "não informado".
 */
export interface PedidoDaAbertura {
  prazo: string;
  hora: string;
  urgente: boolean;
  motivo: string;
  observacao: string;
}

export const PEDIDO_VAZIO: PedidoDaAbertura = {
  prazo: '',
  hora: '',
  urgente: false,
  motivo: '',
  observacao: '',
};

/** O pedido gravado no processo, como a tela o edita (a hora sem os segundos). */
export function pedidoDoProcesso(processo: ProcessoData): PedidoDaAbertura {
  return {
    prazo: processo.prazo_processual ?? '',
    hora: (processo.prazo_processual_hora ?? '').slice(0, 5),
    urgente: processo.urgencia_solicitada ?? false,
    motivo: processo.motivo_da_urgencia ?? '',
    observacao: processo.observacao ?? '',
  };
}

/** O que a tela Enviar documentos edita além dos arquivos (E144.F3). */
export interface FormularioDoEnvio {
  identificacao: IdentificacaoMinima;
  pedido: PedidoDaAbertura;
}

export type ErroDoPedido = 'hora_sem_prazo' | 'urgencia_sem_motivo';

/** A mesma coerência do servidor: hora só com a data; urgência só com o motivo. */
export function validarPedido(pedido: PedidoDaAbertura): ErroDoPedido | null {
  if (pedido.hora.length > 0 && pedido.prazo.length === 0) return 'hora_sem_prazo';
  if (pedido.urgente && pedido.motivo.trim().length === 0) return 'urgencia_sem_motivo';
  return null;
}

/**
 * O `PATCH` do processo com só o que mudou na identificação e no pedido; `null` sem mudança. O
 * número segue o formato do servidor (vazio é nulo).
 */
export function mudancasDoProcesso(
  processo: ProcessoData,
  identificacao: IdentificacaoMinima,
  pedido: PedidoDaAbertura,
): ProcessoUpdateData | null {
  const novo: Required<ProcessoUpdateData> = {
    pasta: textoOuNulo(identificacao.pasta),
    process_number: textoOuNulo(identificacao.processo),
    cpf_informado: cpfOuNulo(identificacao.cpf),
    prazo_processual: textoOuNulo(pedido.prazo),
    prazo_processual_hora: textoOuNulo(pedido.hora),
    urgencia_solicitada: pedido.urgente,
    motivo_da_urgencia: pedido.urgente ? textoOuNulo(pedido.motivo) : null,
    observacao: textoOuNulo(pedido.observacao),
  };
  const gravado = pedidoDoProcesso(processo);
  const antes: Required<ProcessoUpdateData> = {
    pasta: processo.pasta,
    process_number: processo.process_number,
    cpf_informado: processo.cpf_informado ?? null,
    prazo_processual: textoOuNulo(gravado.prazo),
    prazo_processual_hora: textoOuNulo(gravado.hora),
    urgencia_solicitada: gravado.urgente,
    motivo_da_urgencia: textoOuNulo(gravado.motivo),
    observacao: textoOuNulo(gravado.observacao),
  };
  const campos = (Object.keys(novo) as (keyof ProcessoUpdateData)[]).filter(
    (campo) => novo[campo] !== antes[campo],
  );
  if (campos.length === 0) return null;
  return Object.fromEntries(campos.map((campo) => [campo, novo[campo]])) as ProcessoUpdateData;
}
